import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0f1115',
    color: '#e6e6e6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '2rem',
    boxSizing: 'border-box',
  },
  container: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #2a2f3a',
  },
  titleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.85rem',
  },
  avatar: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    border: '1px solid #2a2f3a',
    objectFit: 'cover',
  },
  titleStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.15rem',
  },
  title: {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: 600,
  },
  email: {
    fontSize: '0.9rem',
    color: '#8a90a0',
  },
  button: {
    background: '#2ea44f',
    color: 'white',
    border: 'none',
    padding: '0.6rem 1.2rem',
    borderRadius: '6px',
    fontSize: '0.95rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  logoutButton: {
    background: 'transparent',
    color: '#e6e6e6',
    border: '1px solid #4a5060',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  section: {
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#8a90a0',
    marginBottom: '0.5rem',
  },
  pre: {
    background: '#161922',
    border: '1px solid #2a2f3a',
    borderRadius: '8px',
    padding: '1rem',
    overflowX: 'auto',
    fontSize: '0.85rem',
    lineHeight: 1.5,
    color: '#d4d4d4',
    margin: 0,
  },
  centered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    gap: '1rem',
  },
  mutedText: {
    color: '#8a90a0',
    fontSize: '0.95rem',
  },
  card: {
    background: '#161922',
    border: '1px solid #2a2f3a',
    borderRadius: '8px',
    padding: '1.25rem',
  },
  radioRow: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
  },
  radioOption: (selected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.6rem 1rem',
    borderRadius: '6px',
    border: `1px solid ${selected ? '#2ea44f' : '#2a2f3a'}`,
    background: selected ? 'rgba(46, 164, 79, 0.08)' : '#0f1115',
    cursor: 'pointer',
    fontSize: '0.95rem',
    color: '#e6e6e6',
    transition: 'border-color 0.15s, background 0.15s',
    userSelect: 'none',
  }),
  statusLine: {
    marginTop: '0.75rem',
    fontSize: '0.85rem',
    minHeight: '1.1em',
  },
  statusSaving: { color: '#8a90a0' },
  statusSaved: { color: '#2ea44f' },
  statusError: { color: '#e06c75' },
};

const RISK_PROFILES = [
  { value: 'aggressive', label: 'Aggressive' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'conservative', label: 'Conservative' },
];

function Dashboard() {
  const {
    isLoading,
    isAuthenticated,
    user,
    loginWithRedirect,
    logout,
    getIdTokenClaims,
    getAccessTokenSilently,
    error,
  } = useAuth0();

  const [idTokenClaims, setIdTokenClaims] = useState(null);
  const [riskProfile, setRiskProfile] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsStatus, setSettingsStatus] = useState(null); // 'saving' | 'saved' | { error: string }

  const apiBase = `https://${process.env.REACT_APP_AUTH0_DOMAIN}/api/v2`;

  useEffect(() => {
    if (isAuthenticated) {
      getIdTokenClaims().then(setIdTokenClaims).catch(() => setIdTokenClaims(null));
    }
  }, [isAuthenticated, getIdTokenClaims]);

  useEffect(() => {
    if (!isAuthenticated || !user?.sub) return;
    let cancelled = false;
    (async () => {
      try {
        setSettingsLoading(true);
        const token = await getAccessTokenSilently();
        const res = await fetch(`${apiBase}/users/${encodeURIComponent(user.sub)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed to load settings (${res.status})`);
        const full = await res.json();
        if (!cancelled) {
          setRiskProfile(full?.user_metadata?.risk_profile || null);
          setSettingsStatus(null);
        }
      } catch (err) {
        if (!cancelled) setSettingsStatus({ error: err.message });
      } finally {
        if (!cancelled) setSettingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, user, getAccessTokenSilently, apiBase]);

  const saveRiskProfile = async (value) => {
    if (value === riskProfile) return;
    const previous = riskProfile;
    setRiskProfile(value);
    setSettingsStatus('saving');
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${apiBase}/users/${encodeURIComponent(user.sub)}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_metadata: { risk_profile: value } }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`${res.status} ${text}`);
      }
      setSettingsStatus('saved');
    } catch (err) {
      setRiskProfile(previous);
      setSettingsStatus({ error: err.message });
    }
  };

  if (isLoading) {
    return (
      <div style={styles.page}>
        <div style={styles.centered}>
          <p style={styles.mutedText}>Loading…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.centered}>
          <h2>Auth error</h2>
          <pre style={styles.pre}>{error.message}</pre>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={styles.page}>
        <div style={styles.centered}>
          <h1 style={{ margin: 0 }}>BakerTrade Portal</h1>
          <p style={styles.mutedText}>Sign in to continue.</p>
          <button style={styles.button} onClick={() => loginWithRedirect()}>
            Log in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.titleGroup}>
            {user?.picture && (
              <img
                src={user.picture}
                alt={user?.name || 'User avatar'}
                style={styles.avatar}
                referrerPolicy="no-referrer"
              />
            )}
            <div style={styles.titleStack}>
              <h1 style={styles.title}>
                Welcome, {user?.name || user?.email}
              </h1>
              {user?.email && user?.name && (
                <span style={styles.email}>{user.email}</span>
              )}
            </div>
          </div>
          <button
            style={styles.logoutButton}
            onClick={() => logout({ logoutParams: { returnTo: `${window.location.origin}/dashboard` } })}
          >
            Log out
          </button>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>Settings</div>
          <div style={styles.card}>
            <div style={{ marginBottom: '0.75rem', fontWeight: 500 }}>Risk profile</div>
            {settingsLoading ? (
              <p style={styles.mutedText}>Loading…</p>
            ) : (
              <div style={styles.radioRow}>
                {RISK_PROFILES.map((opt) => {
                  const selected = riskProfile === opt.value;
                  return (
                    <label key={opt.value} style={styles.radioOption(selected)}>
                      <input
                        type="radio"
                        name="risk-profile"
                        value={opt.value}
                        checked={selected}
                        onChange={() => saveRiskProfile(opt.value)}
                        style={{ accentColor: '#2ea44f' }}
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            )}
            <div style={styles.statusLine}>
              {settingsStatus === 'saving' && <span style={styles.statusSaving}>Saving…</span>}
              {settingsStatus === 'saved' && <span style={styles.statusSaved}>Saved.</span>}
              {settingsStatus && typeof settingsStatus === 'object' && settingsStatus.error && (
                <span style={styles.statusError}>Error: {settingsStatus.error}</span>
              )}
            </div>
          </div>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>User profile</div>
          <pre style={styles.pre}>{JSON.stringify(user, null, 2)}</pre>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>ID token claims</div>
          <pre style={styles.pre}>{JSON.stringify(idTokenClaims, null, 2)}</pre>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
